import { captureException } from 'shared/sentry';
import { CONSENT_TYPES } from '../../utils/config/cookies';
import { resetFirebasePushServiceWorkers, syncFcmTokenWithServer } from '../../utils/firebaseUtils';
import { markFcmOperational, markFcmUnoperational, resetFcmOperationalState } from './fcmChannelHealth';
import { waitForAuthStorageReady } from './fcmAuthStorageReady';
import {
  FCM_SYNC_REASONS,
  PUSH_CONFIG,
  PUSH_SENTRY_STEPS,
  PUSH_SENTRY_TAGS,
} from './pushConstants';
import {
  clearStoredFcmTokens,
  isFcmSessionBootstrapped,
  markFcmSessionBootstrapped,
} from './fcmStorage';
import { resolvePushPermissionState } from './pushPermission';
import { getUnifiedPushState } from './pushState';

const DEBOUNCE_MS = 400;
const FCM_SYNC_RETRY_DELAYS_MS = [0, 800, 2000, 4500, 9000, 15000];

let syncPromise = null;
let debounceId = null;
let tabHiddenAt = null;
let retryTimeoutId = null;
let activeSyncLoopId = 0;

const invalidateFcmSyncRetryLoops = () => {
  activeSyncLoopId += 1;
  if (typeof window !== 'undefined' && retryTimeoutId != null) {
    window.clearTimeout(retryTimeoutId);
  }
  retryTimeoutId = null;
};

export const isEligibleForFcmTokenAttempt = async (options) => {
  const { isUserEligible } = options;

  if (typeof isUserEligible === 'function' && !isUserEligible()) {
    return false;
  }

  const state = getUnifiedPushState();
  const permission = await resolvePushPermissionState();
  return state.isLoggedIn && permission === 'granted' && state.consent === CONSENT_TYPES.ALL;
};

const runFcmSyncIfEligible = async (options, { forcePatch = false } = {}) => {
  const { isUserEligible, updateFcmToken, getCachedToken } = options;

  if (typeof updateFcmToken !== 'function') {
    return null;
  }
  if (typeof isUserEligible === 'function' && !isUserEligible()) {
    return null;
  }

  const state = getUnifiedPushState();
  const permission = await resolvePushPermissionState();
  if (!state.isLoggedIn || permission !== 'granted') {
    return null;
  }
  if (state.consent !== CONSENT_TYPES.ALL) {
    return null;
  }

  return syncFcmTokenWithServer({
    isUserEligible,
    updateFcmToken,
    getCachedToken,
    forcePatch,
  });
};

const shouldForcePatch = (reason) =>
  reason === FCM_SYNC_REASONS.AUTH ||
  reason === FCM_SYNC_REASONS.CONSENT ||
  reason === FCM_SYNC_REASONS.PERMISSION ||
  reason === FCM_SYNC_REASONS.USER_GESTURE ||
  reason === FCM_SYNC_REASONS.RESUME ||
  (reason === FCM_SYNC_REASONS.BOOT && !isFcmSessionBootstrapped());

const shouldSyncOnResume = () => {
  if (tabHiddenAt == null) {
    return false;
  }
  return Date.now() - tabHiddenAt >= PUSH_CONFIG.IDLE_RESYNC_MS;
};

const markTabHidden = () => {
  tabHiddenAt = Date.now();
};

const markTabVisible = () => {
  const wasHiddenLongEnough = shouldSyncOnResume();
  tabHiddenAt = null;
  return wasHiddenLongEnough;
};

/**
 * Clears in-memory sync state on logout so the next login always re-syncs.
 */
export const resetFcmSyncSession = () => {
  if (typeof window !== 'undefined' && debounceId != null) {
    window.clearTimeout(debounceId);
  }
  debounceId = null;
  invalidateFcmSyncRetryLoops();
  syncPromise = null;
  tabHiddenAt = null;
  clearStoredFcmTokens();
  resetFcmOperationalState();
  void resetFirebasePushServiceWorkers();
};

const executeFcmSync = async (options, { reason, forcePatch }) => {
  if (syncPromise) {
    return syncPromise;
  }

  syncPromise = (async () => {
    try {
      const eligible = await isEligibleForFcmTokenAttempt(options);
      const syncedToken = await runFcmSyncIfEligible(options, { forcePatch });
      if (syncedToken) {
        markFcmOperational(true);
        if (
          reason === FCM_SYNC_REASONS.BOOT ||
          reason === FCM_SYNC_REASONS.AUTH
        ) {
          markFcmSessionBootstrapped();
        }
      } else if (forcePatch && eligible) {
        markFcmUnoperational();
        if (typeof console !== 'undefined') {
          console.warn('pushNotification >> FCM sync failed after login/boot.', { reason });
        }
      }
      return syncedToken;
    } catch (error) {
      captureException(error, {
        tags: PUSH_SENTRY_TAGS,
        extra: { step: PUSH_SENTRY_STEPS.INITIALIZER_SYNC, reason },
      });
      return null;
    } finally {
      syncPromise = null;
    }
  })();

  return syncPromise;
};

/**
 * Single entry point for FCM server sync. Coalesces rapid triggers and only PATCHes
 * when required (login, idle resume, token/config change, consent/permission updates).
 *
 * @param {object} options
 * @param {object} [meta]
 * @param {keyof typeof FCM_SYNC_REASONS | string} meta.reason
 * @param {() => void} [meta.onSettled]
 */
export const requestFcmSync = (options, { reason, onSettled } = {}) => {
  if (typeof window === 'undefined') {
    return;
  }

  if (reason === FCM_SYNC_REASONS.RESUME && !shouldSyncOnResume()) {
    if (typeof onSettled === 'function') {
      onSettled();
    }
    return;
  }

  const forcePatch = shouldForcePatch(reason);

  window.clearTimeout(debounceId);
  debounceId = window.setTimeout(() => {
    debounceId = null;
    void executeFcmSync(options, { reason, forcePatch }).finally(() => {
      if (typeof onSettled === 'function') {
        onSettled();
      }
    });
  }, DEBOUNCE_MS);
};

/**
 * Immediate sync (user gesture flows). Returns the sync promise.
 */
export const enqueueFcmSync = (options, { reason = FCM_SYNC_REASONS.USER_GESTURE } = {}) => {
  if (typeof window === 'undefined') {
    return Promise.resolve(null);
  }

  const forcePatch = shouldForcePatch(reason) || reason === FCM_SYNC_REASONS.USER_GESTURE;
  return executeFcmSync(options, { reason, forcePatch });
};

/**
 * Retries FCM sync after login / boot when IndexedDB or the service worker may not be ready yet.
 * Coalesced globally — a new call cancels any in-flight retry loop.
 */
export const scheduleFcmSyncWithRetry = (
  options,
  { reason, onSettled } = {}
) => {
  if (typeof window === 'undefined') {
    return;
  }

  invalidateFcmSyncRetryLoops();
  const loopId = activeSyncLoopId;

  const runAttempt = async (attemptIndex) => {
    if (loopId !== activeSyncLoopId) {
      return;
    }

    if (attemptIndex > 0) {
      await new Promise((resolve) => {
        retryTimeoutId = window.setTimeout(resolve, FCM_SYNC_RETRY_DELAYS_MS[attemptIndex]);
      });
      retryTimeoutId = null;
    }

    if (loopId !== activeSyncLoopId) {
      return;
    }

    await waitForAuthStorageReady();

    if (loopId !== activeSyncLoopId) {
      return;
    }

    const syncedToken = await enqueueFcmSync(options, { reason });
    if (syncedToken) {
      if (typeof onSettled === 'function') {
        onSettled();
      }
      return;
    }

    if (loopId !== activeSyncLoopId) {
      return;
    }

    const nextAttempt = attemptIndex + 1;
    if (nextAttempt >= FCM_SYNC_RETRY_DELAYS_MS.length) {
      if (typeof onSettled === 'function') {
        onSettled();
      }
      return;
    }

    void runAttempt(nextAttempt);
  };

  void runAttempt(0);
};

export const recordFcmTabHidden = () => {
  markTabHidden();
};

export const recordFcmTabVisibleAndShouldSync = () => markTabVisible();
